import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Plus, Search, Pencil, Trash2, Loader2, KeyRound, ShieldCheck, KeySquare, Copy,
} from "lucide-react";
import { toast } from "sonner";
import ModuleSelector from "@/components/admin/ModuleSelector";
import PasswordModal from "@/components/admin/PasswordModal";
import ResetActionsModal from "@/components/admin/ResetActionsModal";

const STATUS_MAP = {
  active: { label: "Aktiivinen", cls: "bg-emerald-500/20 text-emerald-200 border-emerald-500/30" },
  pending: { label: "Odottaa", cls: "bg-amber-500/20 text-amber-200 border-amber-500/30" },
  suspended: { label: "Pysäytetty", cls: "bg-red-500/20 text-red-200 border-red-500/30" },
  expired: { label: "Vanhentunut", cls: "bg-zinc-500/30 text-zinc-200 border-zinc-500/30" },
};

const PAYMENT_LABELS = {
  trial: "Trial",
  paid: "Maksettu",
  unpaid: "Maksamatta",
  comp: "Veloitukseton",
};

function LicenseForm({ license, onSaved, onClose, onShowPassword }) {
  const isEdit = !!license;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    customer_email: license?.customer_email || "",
    customer_name: license?.customer_name || "",
    company_name: license?.company_name || "",
    phone: license?.phone || "",
    modules: license?.modules || ["invoicing", "accounting"],
    seats: license?.seats || 1,
    valid_from: license?.valid_from || new Date().toISOString().split("T")[0],
    valid_until: license?.valid_until || "",
    payment_status: license?.payment_status || "trial",
    monthly_price: license?.monthly_price || 0,
    status: license?.status || "active",
    notes: license?.notes || "",
    password: "",
  });

  const save = async () => {
    if (!form.customer_email || !form.customer_name) {
      return toast.error("Sähköposti ja nimi vaaditaan");
    }
    if (form.modules.length === 0) {
      return toast.error("Valitse vähintään yksi moduuli");
    }
    setLoading(true);
    try {
      if (isEdit) {
        const data = {
          customer_name: form.customer_name,
          company_name: form.company_name || null,
          phone: form.phone || null,
          modules: form.modules,
          seats: form.seats,
          valid_from: form.valid_from || null,
          valid_until: form.valid_until || null,
          payment_status: form.payment_status,
          monthly_price: form.monthly_price,
          status: form.status,
          notes: form.notes || null,
        };
        await api.patch(`/admin/licenses/${license.id}`, data);
        toast.success("Lisenssi päivitetty");
      } else {
        const data = {
          customer_email: form.customer_email.trim().toLowerCase(),
          customer_name: form.customer_name,
          company_name: form.company_name || null,
          phone: form.phone || null,
          modules: form.modules,
          seats: form.seats,
          valid_from: form.valid_from || null,
          valid_until: form.valid_until || null,
          payment_status: form.payment_status,
          monthly_price: form.monthly_price,
          notes: form.notes || null,
        };
        if (form.password && form.password.length >= 6) {
          data.password = form.password;
        }
        const res = await api.post("/admin/licenses", data);
        toast.success(`Lisenssi luotu (${res.data.license_key}) – käyttäjä aktivoituu välittömästi`);
        if (res.data.initial_password) {
          onShowPassword?.({
            email: res.data.customer_email,
            password: res.data.initial_password,
            title: `Lisenssi luotu – ${res.data.license_key}`,
          });
        }
      }
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };

  const periodMrr = (form.monthly_price || 0) * 1;

  return (
    <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1" data-testid="license-form">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label className="text-xs">Asiakkaan sähköposti *</Label>
          <Input
            value={form.customer_email}
            disabled={isEdit}
            onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
            placeholder="asiakas@yritys.fi"
            className="mt-1"
            data-testid="license-email"
          />
          {isEdit && <p className="text-[10px] text-muted-foreground mt-1">Sähköpostia ei voi muuttaa muokkauksessa</p>}
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Yhteyshenkilön nimi *</Label>
          <Input
            value={form.customer_name}
            onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
            className="mt-1"
            data-testid="license-customer-name"
          />
        </div>
        <div>
          <Label className="text-xs">Yrityksen nimi</Label>
          <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Puhelin</Label>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Voimassa alkaen</Label>
          <Input
            type="date"
            value={form.valid_from}
            onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs">Voimassa asti (tyhjä = jatkuva)</Label>
          <Input
            type="date"
            value={form.valid_until}
            onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs">Käyttäjäpaikkoja</Label>
          <Input
            type="number"
            min="1"
            value={form.seats}
            onChange={(e) => setForm({ ...form, seats: parseInt(e.target.value) || 1 })}
            className="mt-1"
            data-testid="license-seats"
          />
        </div>
        <div>
          <Label className="text-xs">Maksun tila</Label>
          <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(PAYMENT_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>
                  {v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Kuukausihinta (€)</Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={form.monthly_price}
            onChange={(e) => setForm({ ...form, monthly_price: parseFloat(e.target.value) || 0 })}
            className="mt-1"
            data-testid="license-price"
          />
          {periodMrr > 0 && (
            <p className="text-[10px] text-emerald-300 mt-1">
              MRR-vaikutus: +{periodMrr.toFixed(2)} €/kk
            </p>
          )}
        </div>
        {isEdit && (
          <div>
            <Label className="text-xs">Lisenssin tila</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_MAP).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="col-span-2 space-y-2">
          <Label className="text-xs">Moduulit lisenssissä</Label>
          <ModuleSelector value={form.modules} onChange={(v) => setForm({ ...form, modules: v })} />
        </div>
        {!isEdit && (
          <div className="col-span-2">
            <Label className="text-xs">Asiakkaan salasana (valinnainen)</Label>
            <div className="flex gap-2 mt-1">
              <Input
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Tyhjä = generoidaan turvallinen automaattisesti"
                className="font-mono"
                data-testid="license-password"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const charset = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%&*";
                  const arr = new Uint32Array(12);
                  crypto.getRandomValues(arr);
                  let out = "";
                  for (let i = 0; i < 12; i++) out += charset[arr[i] % charset.length];
                  setForm({ ...form, password: out });
                }}
              >
                Generoi
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              Vähintään 6 merkkiä. Asiakas voi vaihtaa myöhemmin. 2FA pyydetään automaattisesti ensimmäisellä kirjautumisella.
            </p>
          </div>
        )}
        <div className="col-span-2">
          <Label className="text-xs">Lisätiedot</Label>
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" />
        </div>
      </div>
      <div className="flex gap-2 pt-2 sticky bottom-0 bg-background pb-1">
        <Button variant="outline" onClick={onClose} className="flex-1">
          Peruuta
        </Button>
        <Button
          onClick={save}
          disabled={loading}
          className="flex-1 gap-2 bg-amber-500 hover:bg-amber-400 text-zinc-900 font-medium"
          data-testid="license-save"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          {isEdit ? "Tallenna muutokset" : "Luo lisenssi ja aktivoi"}
        </Button>
      </div>
    </div>
  );
}

export default function LicensesAdmin() {
  const navigate = useNavigate();
  const [licenses, setLicenses] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editLicense, setEditLicense] = useState(null);
  const [pwModal, setPwModal] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [licRes, sRes] = await Promise.all([
        api.get("/admin/licenses"),
        api.get("/admin/stats"),
      ]);
      setLicenses(licRes.data);
      setStats(sRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
      if (e?.response?.status === 403) navigate("/");
    }
    setLoading(false);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const filtered = licenses.filter((l) => {
    if (statusFilter !== "all" && l.status !== statusFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (l.customer_email || "").toLowerCase().includes(q) ||
      (l.customer_name || "").toLowerCase().includes(q) ||
      (l.company_name || "").toLowerCase().includes(q) ||
      (l.license_key || "").toLowerCase().includes(q)
    );
  });

  const resetPw = (l) => {
    setResetTarget({ kind: "license", id: l.id, email: l.customer_email });
  };

  const remove = async (l) => {
    if (!window.confirm(`Poistetaanko lisenssi ${l.license_key} pysyvästi?`)) return;
    try {
      await api.delete(`/admin/licenses/${l.id}`);
      toast.success("Lisenssi poistettu");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyKey = (k) => {
    navigator.clipboard.writeText(k);
    toast.success("Lisenssiavain kopioitu");
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6" data-testid="licenses-admin">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-amber-100">Lisenssit</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Hallitse asiakkaiden lisenssejä – luonti aktivoi asiakkaan välittömästi ja näyttää väliaikaiset tunnukset.
          </p>
        </div>
        <Button
          className="gap-2 bg-amber-500 text-zinc-900 hover:bg-amber-400 font-medium"
          onClick={() => {
            setEditLicense(null);
            setShowForm(true);
          }}
          data-testid="licenses-new"
        >
          <Plus className="w-4 h-4" /> Luo lisenssi
        </Button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Aktiiviset" value={stats.active_licenses ?? 0} accent="emerald" />
        <KPI label="Odottavat" value={stats.pending_licenses ?? 0} accent="amber" />
        <KPI label="Lisenssiasiakkaita" value={stats.license_customers ?? 0} accent="purple" />
        <KPI label="MRR (€/kk)" value={(stats.mrr ?? 0).toFixed(2)} accent="amber" />
      </div>

      <div className="flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Hae yrityksellä, sähköpostilla, avaimella..."
            className="pl-9"
            data-testid="licenses-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="licenses-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Kaikki tilat</SelectItem>
            {Object.entries(STATUS_MAP).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Ladataan...</div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <KeySquare className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p>Ei lisenssejä. Luo ensimmäinen asiakkaallesi!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-amber-500/20">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-amber-500/20">
                <th className="py-3 px-3">Avain</th>
                <th className="py-3 px-3">Asiakas</th>
                <th className="py-3 px-3">Sähköposti</th>
                <th className="py-3 px-3">Moduulit</th>
                <th className="py-3 px-3 text-center">Paikkoja</th>
                <th className="py-3 px-3 text-right">€/kk</th>
                <th className="py-3 px-3">Tila</th>
                <th className="py-3 px-3 w-48"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => {
                const st = STATUS_MAP[l.status] || STATUS_MAP.pending;
                return (
                  <tr key={l.id} className="border-b border-amber-500/10 hover:bg-amber-500/5 transition" data-testid={`license-row-${l.id}`}>
                    <td className="py-3 px-3">
                      <button
                        onClick={() => copyKey(l.license_key)}
                        className="font-mono text-xs text-amber-300 hover:underline flex items-center gap-1"
                        data-testid={`license-key-${l.id}`}
                      >
                        {l.license_key} <Copy className="w-3 h-3 opacity-60" />
                      </button>
                      <p className="text-[10px] text-muted-foreground mt-1">{PAYMENT_LABELS[l.payment_status]}</p>
                    </td>
                    <td className="py-3 px-3">
                      <p className="font-medium">{l.customer_name}</p>
                      {l.company_name && <p className="text-[11px] text-muted-foreground">{l.company_name}</p>}
                    </td>
                    <td className="py-3 px-3 font-mono text-xs">{l.customer_email}</td>
                    <td className="py-3 px-3">
                      <span className="text-xs text-muted-foreground">{l.modules?.length || 0} moduulia</span>
                    </td>
                    <td className="py-3 px-3 text-center font-mono">{l.seats}</td>
                    <td className="py-3 px-3 text-right font-mono">{(l.monthly_price || 0).toFixed(2)}</td>
                    <td className="py-3 px-3">
                      <Badge className={`text-[10px] ${st.cls}`}>{st.label}</Badge>
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex gap-1 justify-end">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="gap-1 text-xs h-8"
                          onClick={() => resetPw(l)}
                          data-testid={`license-reset-${l.id}`}
                        >
                          <KeyRound className="w-3 h-3" /> Palauta
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="w-8 h-8"
                          onClick={() => {
                            setEditLicense(l);
                            setShowForm(true);
                          }}
                          data-testid={`license-edit-${l.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="w-8 h-8 text-destructive/60 hover:text-destructive"
                          onClick={() => remove(l)}
                          data-testid={`license-delete-${l.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeySquare className="w-5 h-5 text-amber-400" />
              {editLicense ? "Muokkaa lisenssiä" : "Luo uusi lisenssi"}
            </DialogTitle>
          </DialogHeader>
          <LicenseForm
            license={editLicense}
            onSaved={() => {
              setShowForm(false);
              load();
            }}
            onClose={() => setShowForm(false)}
            onShowPassword={(d) => setPwModal(d)}
          />
        </DialogContent>
      </Dialog>

      <PasswordModal
        open={!!pwModal}
        onClose={() => setPwModal(null)}
        email={pwModal?.email}
        password={pwModal?.password}
        title={pwModal?.title}
      />

      <ResetActionsModal
        open={!!resetTarget}
        onClose={() => setResetTarget(null)}
        target={resetTarget}
      />
    </div>
  );
}

function KPI({ label, value, accent = "amber" }) {
  const colour = {
    amber: "text-amber-300",
    emerald: "text-emerald-300",
    purple: "text-purple-300",
    blue: "text-blue-300",
  }[accent] || "text-foreground";
  return (
    <Card className="bg-zinc-900/60 border-amber-500/20">
      <CardContent className="p-4">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className={`text-2xl font-bold mt-1 ${colour}`}>{value}</p>
      </CardContent>
    </Card>
  );
}
